import { clamp } from "./number-utils";
import type { DragBox } from "./types";

/** 落线厚度；几何使条带中心落在单元格边界线上（骑缝） */
const DROP_LINE_ROW_THICKNESS_PX = 2;
const DROP_LINE_COL_THICKNESS_PX = 2;

export function toDragBox(r: DOMRect): DragBox {
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    bottom: r.bottom,
    right: r.right,
  };
}

function intervalsOverlap1D(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 < b1 && a1 > b0;
}

export function rowMoveToIndex(from: number, insertSlotD: number, rowCount: number): number {
  let to = insertSlotD;
  if (from < insertSlotD) to = insertSlotD - 1;
  return clamp(to, 0, rowCount - 1);
}

export function colMoveToIndex(from: number, insertSlotD: number, colCount: number): number {
  let to = insertSlotD;
  if (from < insertSlotD) to = insertSlotD - 1;
  return clamp(to, 0, colCount - 1);
}

/**
 * 行：幽灵条与某行带在竖向上有交集即算「进入该行范围」。
 */
export function resolveRowLineDrag(
  tableDom: HTMLTableElement,
  fromIndex: number,
  clampedDy: number,
  rowBox: DragBox,
  rowCount: number,
): { dropLine: { top: number; left: number; width: number; height: number }; insertSlot: number } | null {
  if (rowCount <= 0 || fromIndex < 0 || fromIndex >= rowCount) return null;
  const ghostTop = rowBox.top + clampedDy;
  const ghostBottom = ghostTop + rowBox.height;

  const overlapping: number[] = [];
  for (let k = 0; k < rowCount; k++) {
    const r = tableDom.rows[k].getBoundingClientRect();
    if (intervalsOverlap1D(ghostTop, ghostBottom, r.top, r.bottom)) overlapping.push(k);
  }

  const downs = overlapping.filter((k) => k > fromIndex);
  const jDown = downs.length > 0 ? Math.max(...downs) : -1;
  const ups = overlapping.filter((k) => k < fromIndex);
  const jUp = ups.length > 0 ? Math.min(...ups) : -1;

  const h = DROP_LINE_ROW_THICKNESS_PX;
  const tRect = tableDom.getBoundingClientRect();
  let insertSlot: number;
  /** 水平落线中心 Y（表格行与行之间的横向边框线） */
  let lineCenterY: number;

  if (clampedDy > 0) {
    if (jDown < 0) return null;
    const r = tableDom.rows[jDown].getBoundingClientRect();
    insertSlot = jDown + 1;
    lineCenterY = r.bottom;
  } else if (clampedDy < 0) {
    if (jUp < 0) return null;
    const r = tableDom.rows[jUp].getBoundingClientRect();
    insertSlot = jUp;
    lineCenterY = r.top;
  } else {
    if (jDown >= 0) {
      const r = tableDom.rows[jDown].getBoundingClientRect();
      insertSlot = jDown + 1;
      lineCenterY = r.bottom;
    } else if (jUp >= 0) {
      const r = tableDom.rows[jUp].getBoundingClientRect();
      insertSlot = jUp;
      lineCenterY = r.top;
    } else {
      return null;
    }
  }

  if (rowMoveToIndex(fromIndex, insertSlot, rowCount) === fromIndex) return null;
  const lineTop = lineCenterY - h / 2;
  return {
    insertSlot,
    dropLine: { top: lineTop, left: tRect.left, width: tRect.width, height: h },
  };
}

export function computeRowDropLine(
  tableDom: HTMLTableElement,
  fromIndex: number,
  clampedDy: number,
  rowBox: DragBox,
): { top: number; left: number; width: number; height: number } | null {
  const n = tableDom.rows.length;
  if (n === 0) return null;
  return resolveRowLineDrag(tableDom, fromIndex, clampedDy, rowBox, n)?.dropLine ?? null;
}

/**
 * 列：幽灵条与某列在横向上有交集即算「进入该列范围」。
 */
export function resolveColLineDrag(
  tableDom: HTMLTableElement,
  fromIndex: number,
  clampedDx: number,
  colBox: DragBox,
  colCount: number,
): { dropLine: { left: number; top: number; width: number; height: number }; insertSlot: number } | null {
  if (colCount <= 0 || fromIndex < 0 || fromIndex >= colCount) return null;
  const ghostLeft = colBox.left + clampedDx;
  const ghostRight = ghostLeft + colBox.width;
  const firstRow = tableDom.rows[0];
  if (!firstRow) return null;

  const overlapping: number[] = [];
  for (let k = 0; k < colCount; k++) {
    const cell = firstRow.cells[k];
    if (!cell) continue;
    const r = cell.getBoundingClientRect();
    if (intervalsOverlap1D(ghostLeft, ghostRight, r.left, r.right)) overlapping.push(k);
  }

  const rights = overlapping.filter((k) => k > fromIndex);
  const jRight = rights.length > 0 ? Math.max(...rights) : -1;
  const lefts = overlapping.filter((k) => k < fromIndex);
  const jLeft = lefts.length > 0 ? Math.min(...lefts) : -1;

  const w = DROP_LINE_COL_THICKNESS_PX;
  const tRect = tableDom.getBoundingClientRect();
  let insertSlot: number;
  /** 竖落落线中心 X（列与列之间的竖向边框线） */
  let lineCenterX: number;

  if (clampedDx > 0) {
    if (jRight < 0) return null;
    const cell = firstRow.cells[jRight];
    if (!cell) return null;
    insertSlot = jRight + 1;
    lineCenterX = cell.getBoundingClientRect().right;
  } else if (clampedDx < 0) {
    if (jLeft < 0) return null;
    const cell = firstRow.cells[jLeft];
    if (!cell) return null;
    insertSlot = jLeft;
    lineCenterX = cell.getBoundingClientRect().left;
  } else {
    if (jRight >= 0) {
      const cell = firstRow.cells[jRight];
      insertSlot = jRight + 1;
      lineCenterX = cell!.getBoundingClientRect().right;
    } else if (jLeft >= 0) {
      const cell = firstRow.cells[jLeft];
      insertSlot = jLeft;
      lineCenterX = cell!.getBoundingClientRect().left;
    } else {
      return null;
    }
  }

  if (colMoveToIndex(fromIndex, insertSlot, colCount) === fromIndex) return null;
  const lineLeft = lineCenterX - w / 2;
  return {
    insertSlot,
    dropLine: { left: lineLeft, top: tRect.top, width: w, height: tRect.height },
  };
}

export function computeColDropLine(
  tableDom: HTMLTableElement,
  fromIndex: number,
  clampedDx: number,
  colBox: DragBox,
  colCount: number,
): { left: number; top: number; width: number; height: number } | null {
  if (colCount <= 0) return null;
  return resolveColLineDrag(tableDom, fromIndex, clampedDx, colBox, colCount)?.dropLine ?? null;
}
